// ═══ Runtime Configuration ═══
// Allows the API URL to be injected at runtime (e.g., via nginx envsubst in Docker)
// instead of being burned into the build at compile time.
//
// Priority: window.__BRAINDOG_CONFIG__ > VITE_* env vars > auto-detect

interface BraindogConfig {
  API_URL: string;
  // URL base de Supabase (Kong :8000). En self-hosted multi-red se deriva del
  // host por el que entró el browser, para que funcione desde cualquier LAN o
  // Tailscale sin hardcodear una IP. Se puede fijar con VITE_SUPABASE_URL
  // (p.ej. un dominio de producción).
  SUPABASE_URL: string;
  // Base WHEP de MediaMTX (relay) para el feed en vivo. Debe apuntar a la IP
  // LAN del host del relay (no localhost), p.ej. http://192.168.3.5:8889.
  RELAY_WHEP_BASE: string;
  // Base del Worker de Cloudflare que arma los playlist HLS históricos
  // (/api/play). Se usa para reproducir el clip de un log al pulsar su
  // timecode en la biblioteca.
  RELAY_WORKER_BASE: string;
  // project_id del relay (config/streams.json). NO es el project id de
  // braindog: identifica el stream grabado en el relay.
  RELAY_PROJECT_ID: string;
  // Canal por defecto cuando no se puede derivar de los tags del log.
  RELAY_DEFAULT_CHANNEL: string;
  // Mapa substring-de-tag → channel (p.ej. {"secundario":"video-2"}).
  // Permite mapear el tag de stream/cámara del log al canal del relay.
  RELAY_CHANNEL_MAP: Record<string, string>;
}

declare global {
  interface Window {
    __BRAINDOG_CONFIG__?: Partial<BraindogConfig>;
  }
}

function resolveApiUrl(): string {
  // 1. Runtime injection (Docker/nginx sets this in index.html)
  const injected = window.__BRAINDOG_CONFIG__?.API_URL;
  if (injected && !injected.includes("${")) {
    return injected;
  }

  // 2. Vite build-time env var
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // 3. Auto-detect: dev server on :5174 → backend on :3005, else same origin
  const isDev = window.location.port === "5174";
  return isDev ? `http://${window.location.hostname || "localhost"}:3005` : "";
}

function resolveSupabaseUrl(): string {
  // 1. Runtime injection (Docker/nginx sets this in index.html)
  const injected = window.__BRAINDOG_CONFIG__?.SUPABASE_URL;
  if (injected && !injected.includes("${")) {
    return injected;
  }

  // 2. Override explícito por env (p.ej. dominio de producción con HTTPS).
  //    Se ignora si apunta a localhost/127.0.0.1: en self-hosted eso rompería
  //    el acceso desde otra máquina, así que preferimos derivar del host real.
  const envUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (envUrl && !/localhost|127\.0\.0\.1/.test(envUrl)) {
    return envUrl;
  }

  // 3. Self-hosted: mismo host que el browser, puerto de Kong (8000). Funciona
  //    entres por la IP de LAN que entres o por Tailscale.
  return `${window.location.protocol}//${window.location.hostname || "localhost"}:8000`;
}

function resolveRelayWhepBase(): string {
  // 1. Runtime injection (Docker/nginx sets this in index.html)
  const injected = window.__BRAINDOG_CONFIG__?.RELAY_WHEP_BASE;
  if (injected && !injected.includes("${")) {
    return injected;
  }

  // 2. Vite build-time env var
  if (import.meta.env.VITE_RELAY_WHEP_BASE) {
    return import.meta.env.VITE_RELAY_WHEP_BASE;
  }

  // 3. Prod (página servida por HTTPS): usar el proxy same-origin del nginx del
  //    frontend (/relay-whep → MediaMTX :8889 del droplet). Evita el bloqueo por
  //    mixed-content de hacer POST http:// a la IP pelada desde una página https.
  //    El media de WebRTC (UDP/DTLS) sigue yendo directo del droplet al browser.
  //    Ver docker/entrypoint.sh (bloque location /relay-whep/).
  if (window.location.protocol === "https:") {
    return "/relay-whep";
  }

  // 4. Dev/LAN (http): mismo host que el browser, puerto WHEP de MediaMTX (8889).
  //    Sirve cuando braindogv2 corre en la misma máquina/LAN que el relay.
  return `http://${window.location.hostname || "localhost"}:8889`;
}

function resolveRelayWorkerBase(): string {
  // 1. Runtime injection (Docker/nginx sets this in index.html)
  const injected = window.__BRAINDOG_CONFIG__?.RELAY_WORKER_BASE;
  if (injected && !injected.includes("${")) return injected;

  // 2. Override explícito por env (p.ej. dominio HTTPS de producción). Se ignora
  //    si apunta a localhost/127.0.0.1: en self-hosted eso rompería el acceso
  //    desde otra máquina, así que preferimos derivar del host real del browser.
  const envUrl = import.meta.env.VITE_RELAY_WORKER_BASE as string | undefined;
  if (envUrl && !/localhost|127\.0\.0\.1/.test(envUrl)) return envUrl;

  // 3. Self-hosted LAN (http): relay-api + Caddy (:8080) corren en el MISMO host
  //    que sirve Braindog. Derivar del hostname por el que entró el browser (IP
  //    de LAN o Tailscale) garantiza que la ruta exista; Caddy escucha en todas
  //    las interfaces. Igual criterio que Supabase (:8000) y WHEP (:8889).
  if (window.location.protocol !== "https:") {
    return `http://${window.location.hostname || "localhost"}:8080`;
  }

  // 4. Fallback histórico (Worker Cloudflare) para instalaciones servidas por HTTPS.
  return "https://relay-worker.braindog.workers.dev";
}

function resolveRelayProjectId(): string {
  const injected = window.__BRAINDOG_CONFIG__?.RELAY_PROJECT_ID;
  if (injected && !injected.includes("${")) return injected;
  if (import.meta.env.VITE_RELAY_PROJECT_ID) return import.meta.env.VITE_RELAY_PROJECT_ID;
  return "fe9aa535-2c10-496b-8c20-1405aa962f08";
}

function resolveRelayChannelMap(): Record<string, string> {
  const raw = import.meta.env.VITE_RELAY_CHANNEL_MAP;
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

const config: BraindogConfig = {
  API_URL: resolveApiUrl(),
  SUPABASE_URL: resolveSupabaseUrl(),
  RELAY_WHEP_BASE: resolveRelayWhepBase(),
  RELAY_WORKER_BASE: resolveRelayWorkerBase(),
  RELAY_PROJECT_ID: resolveRelayProjectId(),
  RELAY_DEFAULT_CHANNEL: import.meta.env.VITE_RELAY_DEFAULT_CHANNEL || "video-1",
  RELAY_CHANNEL_MAP: resolveRelayChannelMap(),
};

export default config;
